// Enterprise Currency Framework (frontend).
// Single source of truth for currency metadata + formatting. The tenant's currency is
// delivered by GET /api/auth/me and pushed into a module-level "active currency" by
// AuthContext, so the many existing pure formatters become tenant-aware without rewiring
// every call site. Components that need reactivity can use the useCurrency() hook.
import { useAuth } from "@/context/AuthContext";

export const SUPPORTED_CURRENCIES = [
  { code: "INR", symbol: "\u20b9", name: "Indian Rupee", locale: "en-IN" },
  { code: "USD", symbol: "$", name: "US Dollar", locale: "en-US" },
  { code: "EUR", symbol: "\u20ac", name: "Euro", locale: "de-DE" },
  { code: "GBP", symbol: "\u00a3", name: "British Pound", locale: "en-GB" },
  { code: "AED", symbol: "\u062f.\u0625", name: "UAE Dirham", locale: "en-AE" },
  { code: "SGD", symbol: "S$", name: "Singapore Dollar", locale: "en-SG" },
  { code: "AUD", symbol: "A$", name: "Australian Dollar", locale: "en-AU" },
  { code: "CAD", symbol: "C$", name: "Canadian Dollar", locale: "en-CA" },
  { code: "JPY", symbol: "\u00a5", name: "Japanese Yen", locale: "ja-JP" },
];

export const DEFAULT_CURRENCY = "INR";
const BY_CODE = Object.fromEntries(SUPPORTED_CURRENCIES.map((c) => [c.code, c]));

export const currencyMeta = (code) =>
  BY_CODE[(code || "").toUpperCase()] || BY_CODE[DEFAULT_CURRENCY];

// Module-level active currency — kept in sync by AuthContext after /me resolves.
let _active = DEFAULT_CURRENCY;
export const setActiveCurrency = (code) => { _active = currencyMeta(code).code; };
export const getActiveCurrency = () => _active;
export const activeSymbol = () => currencyMeta(_active).symbol;

// Full formatted amount, e.g. ₹1,250 / $1,250 / ¥1,250. Integers (no minor units) to
// match the platform's existing display convention.
export function formatCurrency(amount, code = _active) {
  const m = currencyMeta(code);
  try {
    return new Intl.NumberFormat(m.locale, {
      style: "currency", currency: m.code, maximumFractionDigits: 0,
    }).format(Number(amount) || 0);
  } catch {
    return `${m.symbol}${(Number(amount) || 0).toLocaleString()}`;
  }
}

// Compact formatted amount for dashboards/tiles, e.g. ₹1.2M, $3.4K.
export function compactCurrency(amount, code = _active) {
  const m = currencyMeta(code);
  const n = Number(amount) || 0;
  const sign = n < 0 ? "-" : "";
  const abs = Math.abs(n);
  let body;
  if (abs >= 1e9) body = (abs / 1e9).toFixed(1).replace(/\.0$/, "") + "B";
  else if (abs >= 1e6) body = (abs / 1e6).toFixed(1).replace(/\.0$/, "") + "M";
  else if (abs >= 1e3) body = (abs / 1e3).toFixed(1).replace(/\.0$/, "") + "K";
  else return formatCurrency(n, code);
  return `${sign}${m.symbol}${body}`;
}

// Reactive hook for components that render the active currency directly.
export function useCurrency() {
  const auth = useAuth();
  const code = auth?.currency || _active;
  const meta = currencyMeta(code);
  return {
    code,
    symbol: meta.symbol,
    name: meta.name,
    format: (n) => formatCurrency(n, code),
    compact: (n) => compactCurrency(n, code),
  };
}
